import React from 'react';
import {
  EnvironmentalTargetInfo,
  SeaStateReport,
  WeatherReport,
  WindReport,
} from '@ais-ui/websockets';
import { useLocalization } from '@ais-ui/locale-management';
import { useDateTimeFormat } from '../conversions/useDateFormat';
import { formatDateTimeWithTimeZone, isNonEmpty } from '@ais-ui/utils';
import { Separator } from '@ais-ui/ui-core';

interface ItemProps {
  label: string;
  value: string;
}

interface IdentificationProps {
  environmentalInfo: EnvironmentalTargetInfo;
}

interface WindProps {
  weatherReportInfo: WindReport;
}

interface WeatherProps {
  weatherReportInfo: WeatherReport;
}

interface SeaStateProps {
  seaStateReportInfo: SeaStateReport;
}

const EnvironmentalItem = ({ label, value }: ItemProps) => {
  return (
    <div className="flex flex-col gap-1">
      <p className="text-sm font-medium flex-none">{label}</p>
      <p className="text-sm text-gray-500 dark:text-gray-400 flex-grow text-ellipsis overflow-hidden">
        {isNonEmpty(value) ? value : 'Unknown'}
      </p>
    </div>
  );
};

const Identification = ({ environmentalInfo }: IdentificationProps) => {
  const { messages } = useLocalization();
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();
  return (
    <div className="grid grid-cols-2 gap-4">
      <EnvironmentalItem
        label={messages.targetDetailsMmsiLabel}
        value={environmentalInfo.mmsi}
      />
      <EnvironmentalItem
        label={messages.targetDetailsSiteIdLabel}
        value={environmentalInfo.siteId}
      />
      <EnvironmentalItem
        label={messages.targetDetailsStationNameLabel}
        value={environmentalInfo.stationName}
      />
      <EnvironmentalItem
        label={messages.targetDetailsReportTimestampLabel}
        value={formatDateTimeWithTimeZone(environmentalInfo.reportTimestamp)}
      />
      <EnvironmentalItem
        label={messages.targetDetailsLatitudeLabel}
        value={environmentalInfo.latitude.toString()}
      />
      <EnvironmentalItem
        label={messages.targetDetailsLongitudeLabel}
        value={environmentalInfo.longitude.toString()}
      />
      <EnvironmentalItem
        label={messages.targetDetailsDataTimeoutLabel}
        value={environmentalInfo.dataTimeout}
      />
      <EnvironmentalItem
        label={messages.targetDetailsOwnerLabel}
        value={environmentalInfo.owner}
      />
      <EnvironmentalItem
        label={messages.targetDetailsLastOriginLabel}
        value={environmentalInfo.lastOrigin}
      />
    </div>
  );
};

const WindDetailsStatus = ({ weatherReportInfo }: WindProps) => {
  const { messages } = useLocalization();
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();
  return (
    <div className="grid grid-cols-2 gap-4">
      <EnvironmentalItem
        label={messages.targetDetailsAverageWindSpeedLabel}
        value={weatherReportInfo.avgWindSpeed}
      />
      <EnvironmentalItem
        label={messages.targetDetailsWindDirectionLabel}
        value={weatherReportInfo.windDirection}
      />
      <EnvironmentalItem
        label={messages.targetDetailsWindGustLabel}
        value={weatherReportInfo.windGust}
      />
      <EnvironmentalItem
        label={messages.targetDetailsWindGustDirectionLabel}
        value={weatherReportInfo.windGustDirection}
      />
      <EnvironmentalItem
        label={messages.targetDetailsWindSensorDataDescriptionLabel}
        value={weatherReportInfo.windSensorDataDescription}
      />
      <EnvironmentalItem
        label={messages.targetDetailsWindTimestampLabel}
        value={formatDateTimeWithTimeZone(weatherReportInfo.windTimestamp)}
      />
    </div>
  );
};

const WeatherDetailsStatus = ({ weatherReportInfo }: WeatherProps) => {
  const { messages } = useLocalization();
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();
  return (
    <div className="grid grid-cols-2 gap-4">
      <EnvironmentalItem
        label={messages.targetDetailsAirPressureLabel}
        value={weatherReportInfo.airPressure}
      />
      <EnvironmentalItem
        label={messages.targetDetailsAirPressureSensorDataDescriptionLabel}
        value={weatherReportInfo.airPressureSensorDataDesc}
      />
      <EnvironmentalItem
        label={messages.targetDetailsAirPressureTendencyLabel}
        value={weatherReportInfo.airPressureTrend}
      />
      <EnvironmentalItem
        label={messages.targetDetailsAirTemperatureLabel}
        value={weatherReportInfo.airTemperature}
      />
      <EnvironmentalItem
        label={messages.targetDetailsTemperatureSensorDataDescriptionLabel}
        value={weatherReportInfo.airTemperatureSensorDataDesc}
      />
      <EnvironmentalItem
        label={messages.targetDetailsDewPointLabel}
        value={weatherReportInfo.dewPoint}
      />
      <EnvironmentalItem
        label={messages.targetDetailsDewPointSensorDataDescriptionLabel}
        value={weatherReportInfo.dewPointSensorDataDesc}
      />
      <EnvironmentalItem
        label={messages.targetDetailsSalinityLabel}
        value={weatherReportInfo.salinity}
      />
      <EnvironmentalItem
        label={messages.targetDetailsWeatherTimestampLabel}
        value={formatDateTimeWithTimeZone(weatherReportInfo.weatherTimestamp)}
      />
    </div>
  );
};

const SeaStateDetailsStatus = ({ seaStateReportInfo }: SeaStateProps) => {
  const { messages } = useLocalization();
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();
  return (
    <div className="grid grid-cols-2 gap-4">
      <EnvironmentalItem
        label={messages.targetDetailsSeaStateLabel}
        value={seaStateReportInfo.seaState}
      />
      <EnvironmentalItem
        label={messages.targetDetailsSignWaveHeightLabel}
        value={seaStateReportInfo.signWaveHeight}
      />
      <EnvironmentalItem
        label={messages.targetDetailsSwellSensorDataDescriptionLabel}
        value={seaStateReportInfo.swellSensorDataDesc}
      />
      <EnvironmentalItem
        label={messages.targetDetailsSwellDirectionLabel}
        value={seaStateReportInfo.swellDirection}
      />
      <EnvironmentalItem
        label={messages.targetDetailsSwellHeightLabel}
        value={seaStateReportInfo.swellHeight}
      />
      <EnvironmentalItem
        label={messages.targetDetailsSwellPeriodLabel}
        value={seaStateReportInfo.swellPeriod}
      />
      <EnvironmentalItem
        label={messages.targetDetailsTempSensorDataDescriptionLabel}
        value={seaStateReportInfo.temp_sensor_data_desc}
      />
      <EnvironmentalItem
        label={messages.targetDetailsSeaStateTimestampLabel}
        value={formatDateTimeWithTimeZone(seaStateReportInfo.seaStateTimestamp)}
      />
      <EnvironmentalItem
        label={messages.targetDetailsWaterTemperatureLabel}
        value={seaStateReportInfo.waterTemperature}
      />
      <EnvironmentalItem
        label={messages.targetDetailsWaterTemperatureDepthLabel}
        value={seaStateReportInfo.waterTemperatureDepth}
      />
      <EnvironmentalItem
        label={messages.targetDetailsWaveDirectionLabel}
        value={seaStateReportInfo.waveDirection}
      />
      <EnvironmentalItem
        label={messages.targetDetailsWavePeriodLabel}
        value={seaStateReportInfo.wavePeriod}
      />
      <EnvironmentalItem
        label={messages.targetDetailsWaveSensorDataDescriptionLabel}
        value={seaStateReportInfo.waveSensorDataDesc}
      />
    </div>
  );
};

export const EnvironmentalDetailsPanel = ({
  environmentalInfo,
}: {
  environmentalInfo: EnvironmentalTargetInfo;
}) => {
  return (
    <div className="grid gap-6">
      <div className="grid gap-4">
        <div>
          <h3 className="text-lg font-medium">
            Identification and Basic Details
          </h3>
          <Separator className="mt-2"></Separator>
        </div>
        <Identification environmentalInfo={environmentalInfo}></Identification>
      </div>
      {environmentalInfo.windReport && (
        <div className="grid gap-4">
          <div>
            <h3 className="text-lg font-medium">Wind Details</h3>
            <Separator className="mt-2"></Separator>
          </div>
          <WindDetailsStatus
            weatherReportInfo={environmentalInfo.windReport}
          ></WindDetailsStatus>
        </div>
      )}
      {environmentalInfo.weatherReport && (
        <div className="grid gap-4">
          <div>
            <h3 className="text-lg font-medium">Wind Details</h3>
            <Separator className="mt-2"></Separator>
          </div>
          <WeatherDetailsStatus
            weatherReportInfo={environmentalInfo.weatherReport}
          ></WeatherDetailsStatus>
        </div>
      )}
      {environmentalInfo.seaStateReport && (
        <div className="grid gap-4">
          <div>
            <h3 className="text-lg font-medium">Sea State Details</h3>
            <Separator className="mt-2"></Separator>
          </div>
          <SeaStateDetailsStatus
            seaStateReportInfo={environmentalInfo.seaStateReport}
          ></SeaStateDetailsStatus>
        </div>
      )}
    </div>
  );
};
